/**
 * useTimesEditor — a times editor's state: useRowsEditor over record_times
 * rows, latest first, plus what's particular to times — what an edited time
 * was, and times written twice for one instant.
 */
import { computed } from "vue"
import { getBrowserTimeZone } from "@auger/ui/utils/date"
import {
  formatTime,
  storedInstants,
  type TimeKind,
  timeParts,
  type TimeRow,
  todayDay,
} from "@auger/ui/utils/times"
import { type DraftRow, useRowsEditor } from "./useRowsEditor"

export { ADD, type RowsDelta as TimesDelta } from "./useRowsEditor"

/** The columns a time can change in place. */
const COLUMNS = [
  "time_type",
  "title",
  "start_at",
  "end_at",
  "timezone",
  "all_day",
  "rrule",
  "capacity",
] as const

/** Timestamps compare as instants: Hasura returns "+00:00", the editor "Z". */
function sameValue(column: string, a: unknown, b: unknown): boolean {
  if (a == null || b == null) return a == null && b == null
  if (column === "start_at" || column === "end_at") {
    return Date.parse(String(a)) === Date.parse(String(b))
  }
  return a === b
}

const startOf = (row: TimeRow) => Date.parse(row.start_at ?? "") || -Infinity

/**
 * @param rows the field's rows as stored
 * @param kind the kind the field holds, or null where any kind goes
 */
export function useTimesEditor(rows: TimeRow[], kind: TimeKind | null) {
  const editor = useRowsEditor<TimeRow>(rows, {
    columns: COLUMNS,
    same: sameValue,
    // Latest first, as the cell reads, so a new or moved time takes its place.
    order: (a, b) => startOf(b) - startOf(a),
    toEntity: (row) => ({
      timeType: row.time_type,
      startAt: row.start_at,
      endAt: row.end_at ?? null,
      allDay: row.all_day ?? false,
      timezone: row.timezone ?? undefined,
      title: row.title ?? null,
      rrule: row.rrule ?? null,
      capacity: row.capacity ?? null,
    }),
    // A new time is a day near the field's latest ("feb 3" added to January's
    // meeting is that February), or today.
    blank: (listed) => {
      const newKind: TimeKind = kind ?? "event"
      const tz = getBrowserTimeZone()
      const date = (listed[0] ? timeParts(listed[0])?.date : null) ?? todayDay()
      const allDay = newKind !== "hours" && newKind !== "slot"
      return {
        time_type: newKind,
        timezone: tz,
        title: null,
        ...storedInstants({ kind: newKind, date, allDay }, tz),
      }
    },
  })

  /** What an edited time was, when the edit moved it. */
  function wasText(row: DraftRow<TimeRow>): string {
    if (!editor.isEdited(row) || !row.orig) return ""
    const before = formatTime(row.orig).text
    return before === formatTime(row.cur).text ? "" : before
  }

  /** Times that are one instant written twice (often a meeting stored in two
   *  zones). */
  const sameMoment = computed(() => {
    const byInstant = new Map<number, string[]>()
    for (const row of editor.listed.value) {
      if (row.cur.all_day || !row.cur.start_at) continue
      const instant = Date.parse(row.cur.start_at)
      byInstant.set(instant, [...(byInstant.get(instant) ?? []), row.key])
    }
    return new Set([...byInstant.values()].filter((keys) => keys.length > 1).flat())
  })

  return { ...editor, wasText, sameMoment }
}
